import { useState, useEffect } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useCategories } from '../hooks/useCategories';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContactIds: string[];
  onSuccess: () => void;
}

export function BulkEditModal({ isOpen, onClose, selectedContactIds, onSuccess }: BulkEditModalProps) {
  const [categoriaId, setCategoriaId] = useState<string>('');
  const [action, setAction] = useState<'update' | 'delete'>('update');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };
  const { categories } = useCategories();

  useEffect(() => {
    if (isOpen) {
      setCategoriaId('');
      setAction('update');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedContactIds.length === 0) {
      toast.error('Nenhum contato selecionado');
      return;
    }

    if (action === 'update' && !categoriaId) {
      toast.error('Selecione uma categoria');
      return;
    }

    setIsSubmitting(true);

    try {
      if (action === 'update') {
        await api.post('/contatos/bulk/update', {
          contactIds: selectedContactIds,
          updates: {
            categoriaId: categoriaId || null,
          },
        });
        toast.success(`${selectedContactIds.length} contato(s) atualizado(s) com sucesso!`);
        onSuccess();
      } else {
        showConfirm(
          'Excluir Contatos',
          `Tem certeza que deseja excluir ${selectedContactIds.length} contato(s)? Esta ação não pode ser desfeita.`,
          async () => {
            setIsSubmitting(true);
            try {
              await api.post('/contatos/bulk/delete', {
                contactIds: selectedContactIds,
              });
              toast.success(`${selectedContactIds.length} contato(s) excluído(s) com sucesso!`);
              onSuccess();
            } catch (error: unknown) {
              console.error('Erro na operação em massa:', error);
              const errorMessage = error instanceof Error ? error.message : 'Erro ao processar operação em massa';
              toast.error(errorMessage);
            } finally {
              setIsSubmitting(false);
            }
          },
          'danger'
        );
      }
    } catch (error: unknown) {
      console.error('Erro na operação em massa:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar operação em massa';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-[#1e3a5f] rounded-2xl shadow-xl max-w-md w-full p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4 font-oswald text-white">
          Edição em Massa
        </h2>

        <p className="text-sm text-gray-300 mb-6">
          {selectedContactIds.length} contato(s) selecionado(s)
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ação
            </label>
            <div className="relative">
              <select
                value={action}
                onChange={(e) => setAction(e.target.value as 'update' | 'delete')}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                required
              >
                <option value="update" className="bg-[#1e3a5f]">Atualizar Categoria</option>
                <option value="delete" className="bg-[#1e3a5f]">Excluir Contatos</option>
              </select>
            </div>
          </div>

          {action === 'update' && (
            <div>
              <label htmlFor="categoria" className="block text-sm font-medium text-gray-300 mb-2">
                Categoria *
              </label>
              <div className="relative">
                <select
                  id="categoria"
                  value={categoriaId}
                  onChange={(e) => setCategoriaId(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                  required={action === 'update'}
                >
                  <option value="" className="bg-[#1e3a5f]">Selecione uma categoria</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id} className="bg-[#1e3a5f]">
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {action === 'delete' && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
              <p className="text-sm text-red-300">
                ⚠️ Atenção: Esta ação irá excluir permanentemente {selectedContactIds.length} contato(s) e não poderá ser desfeita.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-white/10 rounded-xl text-gray-300 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-xl text-white focus:outline-none focus:ring-2 disabled:opacity-50 transition-colors shadow-lg ${
                action === 'delete'
                  ? 'bg-red-600/80 hover:bg-red-700 focus:ring-red-500 border border-red-500/50'
                  : 'bg-blue-600/80 hover:bg-blue-700 focus:ring-blue-500 border border-blue-500/50'
              }`}
            >
              {isSubmitting ? 'Processando...' : action === 'delete' ? 'Excluir' : 'Atualizar'}
            </button>
          </div>
        </form>
      </div>

      {/* AlertDialog de Confirmação */}
      <AlertDialog.Root open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] max-w-md z-[101] animate-in zoom-in-95 duration-300 focus:outline-none">
            <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 rounded-[2.5rem] border border-white/10 shadow-2xl backdrop-blur-3xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-50" />
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-colors duration-500" />
              
              <div className="relative">
                <AlertDialog.Title className="text-2xl font-bold text-white mb-4 tracking-tight font-oswald uppercase">
                  {confirmDialog.title}
                </AlertDialog.Title>
                
                <AlertDialog.Description className="text-slate-300 mb-8 leading-relaxed text-sm">
                  {confirmDialog.message}
                </AlertDialog.Description>

                <div className="flex justify-end gap-4">
                  <AlertDialog.Cancel asChild>
                    <button className="px-6 py-2.5 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 transition-all duration-300">
                      Cancelar
                    </button>
                  </AlertDialog.Cancel>
                  
                  <AlertDialog.Action asChild>
                    <button
                      onClick={() => {
                        confirmDialog.onConfirm();
                        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                      }}
                      className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${
                        confirmDialog.type === 'danger' 
                          ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-red-500/20' 
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/20'
                      }`}
                    >
                      {confirmDialog.type === 'danger' ? 'Confirmar Exclusão' : 'Confirmar'}
                    </button>
                  </AlertDialog.Action>
                </div>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
